import LoginIcon from '@mui/icons-material/Login';
import { Alert, Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { Navigate, useLocation, useSearchParams } from 'react-router';
import { aanmeldAdres, useAanmeldProviders } from '../api/auth.ts';
import { Laadstatus } from '../componenten/Laadstatus.tsx';
import { aanmeldfoutLabels } from '../helpers/labels.ts';
import { useSessie } from './SessieContext.tsx';

/**
 * De omlijsting van de aanmeldpagina's: een kaart in het midden met het logo.
 * @param props De inhoud van de kaart.
 * @returns De pagina.
 */
export function Aanmeldkader({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={1} sx={{ mb: 3, alignItems: 'center' }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 700,
                fontSize: 22,
              }}
            >
              S
            </Box>
            <Typography variant="h5" component="h1">
              Solvio Works CRM
            </Typography>
          </Stack>
          {children}
        </CardContent>
      </Card>
    </Box>
  );
}

/**
 * De aanmeldpagina met een knop per ingeschakelde aanmeldmethode. Aanmelden is een navigatie naar de API, die
 * doorstuurt naar de provider en daarna terugkeert op de pagina waar de gebruiker heen wilde.
 * @returns De pagina.
 */
export function AanmeldPagina() {
  const { ik } = useSessie();
  const [params] = useSearchParams();
  const locatie = useLocation();
  const providers = useAanmeldProviders();
  const van = (locatie.state as { van?: string } | null)?.van ?? '/';
  const foutcode = params.get('fout');

  if (ik) {
    return <Navigate to={van} replace />;
  }

  return (
    <Aanmeldkader>
      {foutcode && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {aanmeldfoutLabels[foutcode] ?? `Aanmelden is mislukt (${foutcode}).`}
        </Alert>
      )}
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Meld je aan met het account van je organisatie.
      </Typography>
      <Laadstatus query={providers}>
        {(lijst) =>
          lijst.length === 0 ? (
            <Alert severity="warning">
              Er is nog geen aanmeldmethode ingeschakeld. Een beheerder stelt die in op de server.
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              {lijst.map((provider) => (
                <Button
                  key={provider.sleutel}
                  href={aanmeldAdres(provider.aanmeldUrl, van)}
                  variant="contained"
                  size="large"
                  startIcon={<LoginIcon />}
                  fullWidth
                >
                  Aanmelden met {provider.naam}
                </Button>
              ))}
            </Stack>
          )
        }
      </Laadstatus>
    </Aanmeldkader>
  );
}
